// Paid Audit Service
// Purpose: Manage paid audit orders and payment verification

import { supabase } from '../lib/supabase';

// ─── Request ID Generator ─────────────────────────────────────────────────────
// Format: REQ-20260305-A3K9
const generateRequestId = () => {
  const d = new Date();
  const date = [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
  ].join('');
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `REQ-${date}-${random}`;
};

export const paidAuditService = {

  // ─── Create a paid audit order record ──────────────────────────────────────
  async createPaymentOrder(orderData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const order = {
        user_id:           user?.id,
        submission_id:     orderData?.submissionId || null,
        request_id:        orderData?.requestId    || null,
        razorpay_order_id: orderData?.razorpayOrderId,
        amount:            orderData?.amount,
        currency:          orderData?.currency,
        payment_status:    'pending',
        customer_email:    orderData?.customerEmail,
        customer_name:     orderData?.customerName,
        // ✅ FIX: column is customer_phone, map from either phone or mobile
        customer_phone:    orderData?.customerPhone || orderData?.customerMobile || '',
      };

      const { data, error } = await supabase
        ?.from('paid_audit_orders')
        ?.insert([order])
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating payment order:', error);
      throw error;
    }
  },

  // ─── Update payment order with successful payment details ──────────────────
  async updatePaymentOrder(orderId, paymentData) {
    try {
      const updateData = {
        razorpay_payment_id: paymentData?.razorpay_payment_id,
        razorpay_signature:  paymentData?.razorpay_signature || null,
        payment_status:      'captured',
        paid_at:             new Date()?.toISOString(),
        error_code:          null,
        error_description:   null,
      };

      const { data, error } = await supabase
        ?.from('paid_audit_orders')
        ?.update(updateData)
        ?.eq('id', orderId)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating payment order:', error);
      throw error;
    }
  },

  // ─── Mark payment as failed ────────────────────────────────────────────────
  async markPaymentFailed(orderId, errorData) {
    try {
      const updateData = {
        payment_status:    'failed',
        error_code:        errorData?.code        || 'unknown',
        error_description: errorData?.description || 'Payment failed',
      };

      const { data, error } = await supabase
        ?.from('paid_audit_orders')
        ?.update(updateData)
        ?.eq('id', orderId)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error marking payment as failed:', error);
      throw error;
    }
  },

  // ─── Mark payment as cancelled ─────────────────────────────────────────────
  async markPaymentCancelled(orderId, errorData) {
    try {
      const updateData = {
        payment_status:    'cancelled',
        error_code:        'payment_cancelled',
        error_description: errorData?.description || 'Payment was cancelled by user',
      };

      const { data, error } = await supabase
        ?.from('paid_audit_orders')
        ?.update(updateData)
        ?.eq('id', orderId)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error marking payment as cancelled:', error);
      throw error;
    }
  },

  // ─── ✅ NEW: Update user subscription status after successful payment ───────
  // Called immediately after payment is captured so admin panel +
  // profile screen show "paid" without waiting for a manual DB fix.
  // Non-throwing — a failure here must never break the success flow
  // because the money has already been received.
  async updateUserSubscription(userId, status = 'paid') {
    try {
      const { error } = await supabase
        ?.from('user_profiles')
        ?.update({ subscription_status: status })
        ?.eq('id', userId);

      if (error) {
        // Log but don't throw — payment already succeeded
        console.error('Failed to update subscription_status:', error);
      }
    } catch (error) {
      console.error('updateUserSubscription error:', error);
      // Intentionally not re-throwing
    }
  },

  // ─── Create paid deck submission BEFORE payment ────────────────────────────
  async createPaidSubmissionBeforePayment(submissionData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const submission = {
        request_id:       generateRequestId(),
        user_id:          user?.id,
        title:            submissionData?.title,
        industry:         submissionData?.industry,
        audience:         submissionData?.audience,
        purpose:          submissionData?.purpose,
        notes:            submissionData?.notes,
        file_name:        submissionData?.fileName,
        file_size:        submissionData?.fileSize,
        file_type:        submissionData?.fileType,
        file_url:         submissionData?.fileUrl,
        slide_count:      submissionData?.slideCount,
        status:           'pending',
        priority:         'high',
        audit_type:       'paid',
        payment_order_id: null,
      };

      const { data, error } = await supabase
        ?.from('deck_submissions')
        ?.insert([submission])
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating paid submission before payment:', error);
      throw error;
    }
  },

  // ─── Mark submission as paid after successful payment ─────────────────────
  async markSubmissionAsPaid(submissionId, paymentOrderId) {
    try {
      const { data, error } = await supabase
        ?.from('deck_submissions')
        ?.update({
          payment_order_id: paymentOrderId,
          status:           'pending',
        })
        ?.eq('id', submissionId)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error marking submission as paid:', error);
      throw error;
    }
  },

  // ─── Get payment order by Razorpay order ID ───────────────────────────────
  async getPaymentOrderByRazorpayId(razorpayOrderId) {
    try {
      const { data, error } = await supabase
        ?.from('paid_audit_orders')
        ?.select('*')
        ?.eq('razorpay_order_id', razorpayOrderId)
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching payment order:', error);
      throw error;
    }
  },

  // ─── Get user's payment orders (with linked submission) ───────────────────
  async getUserPaymentOrders(userId) {
    try {
      const { data, error } = await supabase
        ?.from('paid_audit_orders')
        ?.select(`
          *,
          deck_submissions!paid_audit_orders_submission_id_fkey (
            id,
            request_id,
            title,
            status,
            submitted_at
          )
        `)
        ?.eq('user_id', userId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching user payment orders:', error);
      throw error;
    }
  },

  // ─── Verify payment signature (client-side basic check) ───────────────────
  verifyPaymentSignature(razorpayOrderId, razorpayPaymentId, razorpaySignature) {
    return !!razorpayOrderId && !!razorpayPaymentId && !!razorpaySignature;
  },
};

export default paidAuditService;